


import {
  defineComponent,
  PropType,
  computed,
  reactive,
  toRef,
  watch,
} from '@vue/composition-api';
import type { Location } from '@/models';
import type {
  HokkaidoAirportReportPreWorkInspectionParams,
  HokkaidoAirportReportPreWorkInspectionSkipParams,
} from '@/models/apis/externalReports/hokkaidoAirport/hokkaidoAirportReportRequest';
import type {
  HokkaidoAirportReport,
  HokkaidoAirportVehicleSelection,
} from '@/models/externalReports/hokkaidoAirport/spHokkaidoAirportReport';
import type {
  HokkaidoAirportSettings,
  HokkaidoAirportLabelSp,
} from '@/models/apis/externalReports/hokkaidoAirport/hokkaidoAirportSettings';
import type { DepartureState } from '@/components/Sp/ExternalReports/HokkaidoAirport/types';
import hokkaidoAirportReportApi from '@/apis/externalReports/hokkaidoAirport/hokkaido_airport_report';
import { useCarTypeChoices } from '@/components/Sp/ExternalReports/HokkaidoAirport/composables/useCarTypeChoices';
import {
  useInspectionInput,
} from '@/components/Sp/ExternalReports/HokkaidoAirport/composables/useInspectionInput';
import {
  useInspectionSubmission,
} from '@/components/Sp/ExternalReports/HokkaidoAirport/composables/useInspectionSubmission';
import InspectionForm from '@/components/Sp/ExternalReports/HokkaidoAirport/InspectionForm/index.vue';
import VehicleSelectFields from '@/components/Sp/ExternalReports/HokkaidoAirport/VehicleSelectFields/index.vue';
import StandbyTypeSelectField
  from '@/components/Sp/ExternalReports/HokkaidoAirport/StandbyTypeSelectField/index.vue';
import LocationCheckButton from '@/components/lib/LocationCheckButton.vue';
import {
  resolveInitialStandbyType,
  resolveInitialVehicleSelection,
} from '@/components/Sp/ExternalReports/HokkaidoAirport/utils';

export default defineComponent({
  components: {
    InspectionForm,
    VehicleSelectFields,
    StandbyTypeSelectField,
    LocationCheckButton,
  },
  props: {
    userSettings: {
      type: Object as PropType<HokkaidoAirportSettings>,
      required: true,
    },
    lastStandbyType: {
      type: String,
      required: true,
    },
    // 前回値が無い端末では null。required: true だと null を渡したときに Vue 2 の型チェックで警告が出るため optional にしている
    lastVehicleSelection: {
      type: Object as PropType<HokkaidoAirportVehicleSelection | null>,
      required: false,
      default: null,
    },
    // 位置情報の取得前は null。required: true だと null を渡したときに Vue 2 の型チェックで警告が出るため optional にしている
    currentLocation: {
      type: Object as PropType<Location | null>,
      required: false,
      default: null,
    },
    ensureReport: {
      type: Function as PropType<() => Promise<HokkaidoAirportReport | null>>,
      required: true,
    },
  },
  emits: ['startStandby', 'showErrorOnCommon'],
  setup(props, { emit }) {
    const state = reactive<DepartureState>({
      selectedAirport: '',
      selectedCarType: '',
      selectedStandbyType: '',
      inspectionResults: {},
    });
    const { isRequesting, confirmModalState, submitInspection } = useInspectionSubmission((errorMessage) => {
      emit('showErrorOnCommon', errorMessage);
    });

    const airports = computed<string[]>(() => {
      return props.userSettings.choices.work_routes;
    });
    const standbyTypes = computed<string[]>(() => props.userSettings.choices.standby_types);
    const spLabels = computed<HokkaidoAirportLabelSp>(() => {
      return props.userSettings.sp;
    });

    watch(() => props.lastVehicleSelection, (lastSelection) => {
      const { airportName, carType } = resolveInitialVehicleSelection(
        lastSelection,
        airports.value,
        props.userSettings.choices.car_types,
      );
      state.selectedAirport = airportName;
      state.selectedCarType = carType;
    }, { immediate: true });
    watch(() => props.lastStandbyType, (standbyType) => {
      state.selectedStandbyType = resolveInitialStandbyType(standbyType, standbyTypes.value);
    }, { immediate: true });

    const { carTypes } = useCarTypeChoices(toRef(props, 'userSettings'), state);
    const { hasVehicleSelection, isAllInspected } = useInspectionInput(toRef(props, 'userSettings'), state);
    // 現在地の有無は LocationCheckButton が判定する
    const canComplete = computed<boolean>(() => {
      return !isRequesting.value && hasVehicleSelection.value && isAllInspected.value;
    });

    const savePreWorkInspection = async(
      save: (reportId: number) => Promise<HokkaidoAirportReport>,
      errorMessage: string,
    ): Promise<void> => {
      await submitInspection(async() => {
        // マウント時に現在地が取れず報告書が未作成の場合は、ここで作成する
        const baseReport = await props.ensureReport();
        if (!baseReport) {
          emit('showErrorOnCommon', '報告書を作成できません。再度操作を行ってください。');
          return;
        }

        const report = await save(baseReport.id);
        emit('startStandby', props.userSettings.choices.status.running.key, report);
      }, errorMessage);
    };

    const completePreWorkInspection = async(): Promise<void> => {
      const params: HokkaidoAirportReportPreWorkInspectionParams = {
        airport_name: state.selectedAirport,
        car_type: state.selectedCarType,
        standby_type: state.selectedStandbyType,
        inspection_results: state.inspectionResults,
      };
      await savePreWorkInspection(
        reportId => hokkaidoAirportReportApi.completePreWorkInspection(reportId, params),
        '開始前点検の保存に失敗しました。再度操作を行ってください。',
      );
    };

    const skipPreWorkInspection = async(): Promise<void> => {
      const params: HokkaidoAirportReportPreWorkInspectionSkipParams = {
        airport_name: state.selectedAirport,
        car_type: state.selectedCarType,
        standby_type: state.selectedStandbyType,
      };
      await savePreWorkInspection(
        reportId => hokkaidoAirportReportApi.skipPreWorkInspection(reportId, params),
        '開始前点検のスキップに失敗しました。再度操作を行ってください。',
      );
    };

    return {
      state,
      confirmModalState,
      isRequesting,
      spLabels,
      airports,
      carTypes,
      standbyTypes,
      hasVehicleSelection,
      canComplete,
      completePreWorkInspection,
      skipPreWorkInspection,
    };
  },
});
